'use client';
import AffirmCTA from '@components/AffirmCTA';
import React, { useContext } from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import { useCurrencyString } from '@hooks/useCurrencyString';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { LinkProps } from '@repo/ui/src/types/link';
import { classNames } from '@repo/ui/src/utils/classNames';
import Product from '@models/product';
import { ProductContext } from '@context/ProductProvider';
import CallToActionButton from '@components/Product/CallToActionButton';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import AlternateSizes from '../AlternateGrillSizes';
import AlternateColors, { COLOR_STYLES } from './_AlternateColors';
import AdditionalInformation from './_AdditionalInformation';
import EssentialOrSaleItems from './_EssentialOrSaleItems';

export interface ProductColor {
  color: keyof typeof COLOR_STYLES;
  link: LinkProps;
  sku: string;
}

export interface AlternateColorProps {
  colors: Array<ProductColor>;
}
interface Props {
  template: PDPTemplate;
  essentials?: Array<{
    id: string;
  }>;
  product: Product;
  dark?: boolean;
  promoMarkdown?: string;
  alternateSize?: Product;
  alternateColors?: {
    colors: Array<ProductColor>;
  };
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
}

const ProductSelection = ({
  template,
  product,
  ownerManual,
  prop65,
  dark,
  promoMarkdown,
  alternateSize,
  alternateColors,
}: Props) => {
  const { eCommEnabled, locale } = useCurrentUrl();

  const { t } = useTranslation();
  const getCurrencyString = useCurrencyString();
  const {
    isLoading: _isLoadingProductList,
    toggleProduct,
    addOnProductGroups,
    totalPrice: _totalPrice,
    addToCart,
    isLoadingAddToCart,
  } = useContext(ProductContext);

  const [quantity, setQuantity] = React.useState(1);
  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + item.price, 0);
  }, [_totalPrice, product]);

  const colorsStep = alternateSize ? 2 : 1;
  const essentialsStep = colorsStep + (alternateColors?.colors?.length ? 1 : 0);
  return (
    <div>
      <ProductHeading
        product={product}
        showPrice={![PDPTemplate.Grill, PDPTemplate.PDPV2].includes(template)}
        dark={dark}
        className="md:tw-block tw-hidden"
      />
      {alternateSize && (
        <AlternateSizes alternateSize={alternateSize} product={product} dark={dark} />
      )}
      {(alternateColors?.colors?.length ?? 0) > 0 && (
        <AlternateColors
          color={product.color}
          colors={alternateColors!.colors}
          dark={dark}
          step={colorsStep}
        />
      )}
      {addOnProductGroups
        ?.filter((group) => group.addons.length)
        ?.map((group, i) => (
          <EssentialOrSaleItems
            key={group.label}
            label={group.label}
            addons={group.addons}
            handleSelect={toggleProduct}
            loading={_isLoadingProductList}
            dark={dark}
            step={essentialsStep + i}
          />
        ))}
      <div className="tw-w-full">
        {eCommEnabled && (
          <>
            {template === PDPTemplate.Grill && (
              <p className="tw-flex tw-between tw-pt-2">
                <span className="tra-text-web-heading-6">
                  {t('productDetailPageContent.estimatedTotalText', undefined, {
                    default: 'Estimated Total',
                  })}
                </span>
                <span className="tra-text-web-heading-3">{getCurrencyString(totalPrice)}</span>
              </p>
            )}
            <div className="tw-text-center tw-py-4">
              {!!totalPrice && locale === 'en-US' && (
                <AffirmCTA className={dark && 'tw-text-white affirm-dark'} amount={totalPrice} />
              )}
            </div>
          </>
        )}
        <div
          className={classNames(
            'tw-flex tw-items-stretch',
            product.ctaButtonType !== 'noCTA' && 'tw-mb-8',
          )}
        >
          {![PDPTemplate.Grill, PDPTemplate.PDPV2, PDPTemplate.PDPV2Light].includes(template) &&
            !product.ctaButtonType && (
              <div className="tw-flex tw-flex-grow tw-mr-4">
                <div className="tw-aspect-square tw-w-15 tw-border-neutral-300 tw-border-2 tw-bg-white tw-flex tw-align-center tw-p-1">
                  <label className="tra-text-web-body-m tw-text-neutral-500 tw-text-center">
                    {t('productDetailPageContent.quantityInputLabel', undefined, {
                      default: 'Qty',
                    })}
                    <input
                      min={1}
                      max={999}
                      type="number"
                      value={quantity}
                      className="tw-text-neutral-600 tw-justify-center tw-w-full tw-pt-2 tw-text-center"
                      onChange={(e) => {
                        const newQuantity = Number(e.target.value);
                        setQuantity(isNaN(newQuantity) ? 0 : newQuantity);
                      }}
                      data-testid="quantityInput"
                    />
                  </label>
                </div>
              </div>
            )}
          {eCommEnabled && (
            <CallToActionButton
              product={product}
              buttonProcessing={isLoadingAddToCart}
              processingText=""
              customProductPrice={totalPrice}
              buttonProps={{
                disabled: isLoadingAddToCart,
                full: true,
                tabIndex: 0,
                className: isLoadingAddToCart
                  ? 'disabled:tw-bg-brand-300'
                  : 'disabled:tw-bg-warm-400',
              }}
              onClickAddToCart={() => addToCart(quantity)}
            />
          )}
        </div>
        {eCommEnabled && promoMarkdown && (
          <div
            className="dangerously-set-markdown-body tw-break-words"
            dangerouslySetInnerHTML={{ __html: promoMarkdown }}
          />
        )}
      </div>
      <AdditionalInformation
        dark={dark}
        ownerManual={ownerManual}
        prop65={prop65}
        showGrillAssistanceInfo={[
          PDPTemplate.Grill,
          PDPTemplate.PDPV2,
          PDPTemplate.PDPV2Light,
        ].includes(template)}
      />
    </div>
  );
};

export default ProductSelection;
